import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Form,
  Switch,
  InputNumber,
  Button,
  Alert,
  Descriptions,
  Space,
  Typography,
  Spin,
  Divider,
  Tag,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchApprovalConfigRequest,
  updateApprovalConfigRequest,
} from '../../../redux/actions/approvalActions';

const { Text, Title } = Typography;

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const ApprovalConfig = () => {
  const dispatch = useDispatch();
  const { config, configLoading, error } = useSelector((s) => s.approvals);
  const { user } = useSelector((s) => s.auth);

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchApprovalConfigRequest());
  }, [dispatch]);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        is_enabled: config.is_enabled,
        threshold_amount: config.threshold_amount,
      });
    }
  }, [config, form]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      dispatch(
        updateApprovalConfigRequest({
          is_enabled: values.is_enabled,
          threshold_amount: values.threshold_amount,
        })
      );
    });
  };

  if (configLoading && !config) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={5} style={{ marginBottom: 4 }}>Purchase Order Approval Workflow</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          When enabled, any PO whose total amount exceeds the threshold will require
          manager approval before it can proceed to delivery and billing.
        </Text>
      </div>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} closable />
      )}

      {/* Current Status Banner */}
      {config && (
        <Alert
          type={config.is_enabled ? 'warning' : 'info'}
          showIcon
          icon={config.is_enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          message={
            config.is_enabled
              ? `Approval workflow is ACTIVE — POs above ${fmtMoney(config.threshold_amount)} require approval`
              : 'Approval workflow is DISABLED — all POs are auto-approved'
          }
          style={{ marginBottom: 20 }}
        />
      )}

      {/* Config Form */}
      <Card size="small">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            is_enabled: false,
            threshold_amount: 50000,
          }}
        >
          <Form.Item
            name="is_enabled"
            label="Enable Approval Workflow"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
              style={{ minWidth: 90 }}
            />
          </Form.Item>

          <Form.Item
            name="threshold_amount"
            label={
              <Space size={4}>
                Approval Threshold Amount
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (POs above this amount require approval)
                </Text>
              </Space>
            }
            rules={[
              { required: true, message: 'Threshold amount is required' },
              { type: 'number', min: 1, message: 'Must be greater than 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={2}
              prefix="₹"
              placeholder="50000"
              formatter={(v) => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              parser={(v) => v ? v.replace(/,/g, '') : ''}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={configLoading}
              onClick={handleSave}
            >
              Save Configuration
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Last updated info */}
      {config?.updated_by_name && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="Last Updated By">
              {config.updated_by_name}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated At">
              {config.updated_at ? dayjs(config.updated_at).format('DD MMM YYYY, hh:mm A') : '—'}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      {/* How it works */}
      <Card
        size="small"
        style={{ marginTop: 20, background: '#f9f9ff', borderColor: '#d6e4ff' }}
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: 13 }}>How the Workflow Works</Text>
          </Space>
        }
      >
        <div style={{ fontSize: 13, lineHeight: 1.8, color: '#595959' }}>
          <div>
            1. A new PO is created with a total amount{' '}
            <Tag color="orange">above threshold</Tag>.
          </div>
          <div>
            2. The PO is set to status{' '}
            <Tag color="gold">Pending Approval</Tag> — it cannot proceed to delivery yet.
          </div>
          <div>
            3. A manager with{' '}
            <Tag color="blue">purchase_order/update</Tag> permission opens the{' '}
            <strong>Approval Inbox</strong>.
          </div>
          <div>
            4. If <strong>Approved</strong>: PO status changes to{' '}
            <Tag color="default">Open</Tag> and normal flow resumes.
          </div>
          <div>
            5. If <strong>Rejected</strong>: PO status changes to{' '}
            <Tag color="error">Cancelled</Tag>.
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ApprovalConfig;
